import { useState, useCallback, useRef, useEffect } from 'react';

export const LAST_KNOWN_PORT_INDEX_KEY = 'spp_scanner_last_port_index';

// Web Serial exposes no serial number — USB vendor/product ids (or the BT service
// class uuid) identify the scanner model, not the physical unit.
const getPortLabel = (port) => {
    try {
        const info = port.getInfo();
        if (info.usbVendorId != null) {
            const hex = (n) => Number(n).toString(16).padStart(4, '0');
            return `SPP USB ${hex(info.usbVendorId)}:${hex(info.usbProductId)}`;
        }
        if (info.bluetoothServiceClassId != null) {
            return `SPP BT ${info.bluetoothServiceClassId}`;
        }
        return 'SPP serial';
    } catch (err) {
        console.warn('Error reading port info:', err);
        return 'SPP serial';
    }
};

export const useSPPScanner = ({
    baudRate = 9600,
    dataBits = 8,
    stopBits = 1,
    parity = 'none',
    flowControl = 'none',
    onConnectionChange,
    onDeviceOffline,
}) => {
    const [isConnected, setConnected] = useState(false);
    const [error, setError] = useState(null);
    const [deviceLabel, setDeviceLabel] = useState(null);

    const portRef = useRef(null);
    const readerRef = useRef(null);
    const writerRef = useRef(null);
    const isReadingRef = useRef(false);
    const onScanCallbackRef = useRef(() => {});
    const disconnectHandlerRef = useRef(null);
    const cleanupInProgressRef = useRef(false);
    const isConnectingRef = useRef(false);
    
    const lastActivityRef = useRef(Date.now());
    const offlineCheckIntervalRef = useRef(null);
    const OFFLINE_TIMEOUT = 10000;

    const getLastKnownPortIndex = useCallback(() => {
        try {
            const stored = localStorage.getItem(LAST_KNOWN_PORT_INDEX_KEY);
            const parsed = Number.parseInt(stored, 10);
            return Number.isFinite(parsed) ? parsed : null;
        } catch (err) {
            console.warn('Error reading last known port index:', err);
            return null;
        }
    }, []);

    const saveLastKnownPortIndex = useCallback((index) => {
        try {
            localStorage.setItem(LAST_KNOWN_PORT_INDEX_KEY, String(index));
        } catch (err) {
            console.warn('Error saving last known port index:', err);
        }
    }, []);

    const rememberPortIndex = useCallback(async (port) => {
        try {
            if (!('serial' in navigator)) return;
            const ports = await navigator.serial.getPorts();
            const index = ports.findIndex((p) => p === port);
            if (index >= 0) {
                saveLastKnownPortIndex(index);
            }
        } catch (err) {
            console.warn('Error remembering port index:', err);
        }
    }, [saveLastKnownPortIndex]);

    const setDisconnected = useCallback((reason = 'manual') => {
        if (cleanupInProgressRef.current) return;
        
        setConnected(false);
        onConnectionChange?.(false);
        
        if (reason === 'offline') {
            onDeviceOffline?.();
        }
        
        portRef.current = null;
    }, [onConnectionChange, onDeviceOffline]);

    const cleanupConnection = useCallback(async () => {
        if (cleanupInProgressRef.current) return;
        cleanupInProgressRef.current = true;

        isReadingRef.current = false;
        isConnectingRef.current = false;
        
        if (offlineCheckIntervalRef.current) {
            clearInterval(offlineCheckIntervalRef.current);
            offlineCheckIntervalRef.current = null;
        }

        try {
            if (readerRef.current) {
                await readerRef.current.cancel();
                readerRef.current.releaseLock?.();
            }
        } catch (err) {
            console.warn('Error closing reader:', err);
        }
        readerRef.current = null;

        try {
            if (writerRef.current) {
                writerRef.current.releaseLock?.();
            }
        } catch (err) {
            console.warn('Error closing writer:', err);
        }
        writerRef.current = null;

        try {
            if (portRef.current) {
                if (disconnectHandlerRef.current) {
                    portRef.current.removeEventListener(
                        'disconnect',
                        disconnectHandlerRef.current
                    );
                    disconnectHandlerRef.current = null;
                }
                
                if (portRef.current.readable || portRef.current.writable) {
                    await portRef.current.close();
                }
            }
        } catch (err) {
            console.warn('Error closing current port:', err);
        }

        try {
            if ('serial' in navigator) {
                const allPorts = await navigator.serial.getPorts();
                for (const port of allPorts) {
                    try {
                        if (port.readable || port.writable) {
                            await port.close();
                            console.log('Closed additional serial port');
                        }
                    } catch (err) {
                        console.warn('Error closing additional port:', err);
                    }
                }
            }
        } catch (err) {
            console.warn('Error getting/closing all ports:', err);
        }
        
        portRef.current = null;
        cleanupInProgressRef.current = false;
    }, []);

    const forceDisconnectAllPorts = useCallback(async () => {
        try {
            if ('serial' in navigator) {
                const allPorts = await navigator.serial.getPorts();
                console.log(`Forcing disconnect of ${allPorts.length} serial ports`);
                
                for (const port of allPorts) {
                    try {
                        if (port.readable || port.writable) {
                            await port.close();
                            console.log('Force closed serial port');
                        }
                    } catch (err) {
                        console.warn('Error force closing port:', err);
                    }
                }
            }
        } catch (err) {
            console.warn('Error in forceDisconnectAllPorts:', err);
        }
    }, []);

    const checkDeviceOnline = useCallback(async () => {
        if (!isConnected || !portRef.current) return;
        
        const now = Date.now();
        const timeSinceLastActivity = now - lastActivityRef.current;
        
        if (timeSinceLastActivity > OFFLINE_TIMEOUT) {
            console.warn('Device appears to be offline - no activity for', timeSinceLastActivity, 'ms');
            await cleanupConnection();
            await forceDisconnectAllPorts(); 
            setDisconnected('offline');
        }
    }, [isConnected, cleanupConnection, setDisconnected]);

    const startOfflineMonitoring = useCallback(() => {
        if (offlineCheckIntervalRef.current) {
            clearInterval(offlineCheckIntervalRef.current);
        }
        
        lastActivityRef.current = Date.now();
        offlineCheckIntervalRef.current = setInterval(checkDeviceOnline, 5000); 
    }, [checkDeviceOnline]);

    const readLoop = useCallback(async () => {
        if (!readerRef.current || !isReadingRef.current) return;
        
        let buffer = '';
        
        try {
            while (isReadingRef.current && portRef.current) {
                const { value, done } = await readerRef.current.read();
                
                if (done) break;
                
                if (value) {
                    lastActivityRef.current = Date.now();
                    
                    buffer += value;
                    const lineEndings = ['\r\n', '\n', '\r'];
                    
                    for (const ending of lineEndings) {
                        while (buffer.includes(ending)) {
                            const [line, ...rest] = buffer.split(ending);
                            buffer = rest.join(ending);
                            const code = line.trim();
                            if (code) onScanCallbackRef.current(code);
                        }
                    }
                    
                    if (buffer.length > 3 && /^[!-~\s]+$/.test(buffer)) {
                        const code = buffer.trim();
                        if (code) onScanCallbackRef.current(code);
                        buffer = '';
                    }
                    
                    if (buffer.length > 200) buffer = '';
                }
            }
        } catch (err) {
            console.error('Read loop error:', err);
            await cleanupConnection();
            setDisconnected('error');
        }
    }, [cleanupConnection, setDisconnected]);

    const startReading = useCallback(() => {
        if (!portRef.current || isReadingRef.current) return;
        if (!portRef.current.readable) return;
        
        isReadingRef.current = true;
        
        const textDecoder = new TextDecoderStream();
        
        portRef.current.readable.pipeTo(textDecoder.writable).catch((err) => {
            console.error('Pipe error:', err);
            if (isReadingRef.current) {
                cleanupConnection().then(() => {
                    setDisconnected('error');
                });
            }
        });
        
        readerRef.current = textDecoder.readable.getReader();
        
        if (portRef.current.writable) {
            writerRef.current = portRef.current.writable.getWriter();
        }
        
        readLoop();
        startOfflineMonitoring();
    }, [readLoop, startOfflineMonitoring, cleanupConnection, setDisconnected]);

    const connect = useCallback(async (silentReconnect = false) => {
        // Prevent concurrent connection attempts
        if (isConnectingRef.current) {
            console.log('Connection already in progress, skipping...');
            return;
        }

        try {
            isConnectingRef.current = true;
            setError(null);

            if (!('serial' in navigator)) {
                throw new Error('Web Serial API not supported');
            }

            await cleanupConnection();

            let port = null;

            if (silentReconnect) {
                // Try to get previously authorized ports without showing dialog
                const ports = await navigator.serial.getPorts();
                if (!ports.length) {
                    throw new Error('No previously authorized port found');
                }

                const lastIndex = getLastKnownPortIndex();
                const orderedPorts = lastIndex != null && ports[lastIndex]
                    ? [ports[lastIndex], ...ports.filter((_, idx) => idx !== lastIndex)]
                    : ports;

                for (const candidate of orderedPorts) {
                    try {
                        if (!candidate.readable && !candidate.writable) {
                            await candidate.open({
                                baudRate,
                                dataBits,
                                stopBits,
                                parity,
                                flowControl
                            });
                        }
                        port = candidate;
                        console.log('Connected via silent reconnect');
                        break;
                    } catch (err) {
                        console.warn('Failed to open candidate port, trying next...', err);
                        try {
                            if (candidate.readable || candidate.writable) {
                                await candidate.close();
                            }
                        } catch (closeErr) {
                            console.warn('Error closing failed candidate port:', closeErr);
                        }
                    }
                }

                if (!port) {
                    throw new Error('No available authorized port found');
                }
            } else {
                // Show browser dialog to select device (first time authorization)
                port = await navigator.serial.requestPort();

                if (!port.readable && !port.writable) {
                    await port.open({
                        baudRate,
                        dataBits,
                        stopBits,
                        parity,
                        flowControl
                    });
                }

                await rememberPortIndex(port);
            }

            if (port.readable || port.writable) {
                console.log('Port already open, reusing connection');
            }

            const handlePortDisconnect = async () => {
                console.log('Port physically disconnected');
                await cleanupConnection();
                setDisconnected('physical_disconnect');
            };
            
            port.addEventListener('disconnect', handlePortDisconnect);
            disconnectHandlerRef.current = handlePortDisconnect;

            portRef.current = port;
            setDeviceLabel(getPortLabel(port));
            setConnected(true);
            onConnectionChange?.(true);

            startReading();

            isConnectingRef.current = false;
        } catch (err) {
            isConnectingRef.current = false;
            setConnected(false);
            onConnectionChange?.(false);

            if (err.name === 'NotFoundError' || err.message.includes('User cancelled')) {
                throw err; // Re-throw so caller knows user cancelled
            }

            console.error('Connection error:', err);
            setError(err.message);

            if (portRef.current) {
                await cleanupConnection();
                setDisconnected('error');
            }

            throw err; // Re-throw connection errors so caller knows connection failed
        }
    }, [
        baudRate,
        dataBits,
        stopBits,
        parity,
        flowControl,
        cleanupConnection,
        startReading,
        setDisconnected,
        onConnectionChange,
    ]);

    const disconnect = useCallback(async () => {
        await cleanupConnection();
        await forceDisconnectAllPorts();
        setDisconnected('manual');
    }, [cleanupConnection, forceDisconnectAllPorts, setDisconnected]);

    const registerScanHandler = useCallback((handler) => {
        onScanCallbackRef.current = handler;
    }, []);

    useEffect(() => {
        return () => {
            cleanupConnection().then(() => {
                forceDisconnectAllPorts();
            });
        };
    }, [cleanupConnection, forceDisconnectAllPorts]);

    useEffect(() => {
        const handleBeforeUnload = async () => {
            try {
                if ('serial' in navigator) {
                    const allPorts = await navigator.serial.getPorts();
                    for (const port of allPorts) {
                        try {
                            if (port.readable || port.writable) {
                                await port.close();
                            }
                        } catch (err) {
                            console.warn('Error closing port on page unload:', err);
                        }
                    }
                }
            } catch (err) {
                console.warn('Error in beforeunload cleanup:', err);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    return {
        connect,
        disconnect,
        isConnected,
        error,
        deviceLabel,
        registerScanHandler,
    };
};
